// NOTE: NSAI-7215 — from a spec and what the user has changed, to the numbers
// every formula in the interface reads.
//
// Inputs come first (the user's value, else the spec's), then the derived
// `values` in dependency order. The order is computed once per spec by
// `orderUiValues`, which is also what the schema uses to reject a cycle — so a
// spec that validated can always be resolved, and resolution never recurses.

import { UI_PLOT_VARIABLE } from "../../constants/ui";
import {
  EUiInputKind,
  type IOrderUiValuesProps,
  type IResolveUiScopeProps,
  type IResolveUiValueProps,
  type IWithUiPlotVariableProps,
  type TOrderUiValuesResult,
  type TUiInput,
  type TUiScalar,
} from "../../types/ui";
import { evaluateUiExpression, parseUiExpression } from "./expression";

interface IFindCircularValuesProps {
  unresolved: string[];
  dependencies: ReadonlyMap<string, string[]>;
}

// NOTE: What Kahn's algorithm leaves unordered is the circle plus every value
// that merely reads from it. Peeling off the values nothing unresolved reads
// leaves the circle itself, so the error names only the values to fix.
const findCircularValues = ({
  unresolved,
  dependencies,
}: IFindCircularValuesProps): string[] => {
  const circular = new Set(unresolved);
  let isPeeled = true;

  while (isPeeled) {
    isPeeled = false;

    for (const valueId of circular) {
      const isRead = [...circular].some(
        (readerId) => dependencies.get(readerId)?.includes(valueId) === true,
      );

      if (!isRead) {
        circular.delete(valueId);
        isPeeled = true;
      }
    }
  }

  return [...circular];
};

// NOTE: Kahn's algorithm over the values' references. Every formula is parsed,
// every name it reads must be an input or another value, and the values must
// not depend on each other in a circle. The error names the value to fix.
export const orderUiValues = ({
  values,
  inputIds,
}: IOrderUiValuesProps): TOrderUiValuesResult => {
  const valueIds = Object.keys(values);
  // NOTE: A Set rather than `in` / hasOwn on the object: names come from model
  // text, and `constructor` must never be found on a prototype.
  const valueIdSet = new Set(valueIds);
  const dependencies = new Map<string, string[]>();

  for (const valueId of valueIds) {
    const parsed = parseUiExpression(values[valueId]);

    if (!parsed.success) {
      return { success: false, error: `values.${valueId}: ${parsed.error}` };
    }

    const unknown = parsed.identifiers.find(
      (name) => !inputIds.has(name) && !valueIdSet.has(name),
    );

    if (unknown !== undefined) {
      return {
        success: false,
        error: `values.${valueId}: "${unknown}" is not an input or a value — define it, or fix the spelling`,
      };
    }

    dependencies.set(
      valueId,
      parsed.identifiers.filter((name) => valueIdSet.has(name)),
    );
  }

  const remaining = new Map(
    valueIds.map((valueId) => [
      valueId,
      dependencies.get(valueId)?.length ?? 0,
    ]),
  );
  const order: string[] = [];
  const ready = valueIds.filter((valueId) => remaining.get(valueId) === 0);

  while (ready.length > 0) {
    const current = ready.shift();

    if (current === undefined) {
      break;
    }

    order.push(current);

    for (const valueId of valueIds) {
      if (dependencies.get(valueId)?.includes(current) === true) {
        const left = (remaining.get(valueId) ?? 0) - 1;
        remaining.set(valueId, left);

        if (left === 0) {
          ready.push(valueId);
        }
      }
    }
  }

  if (order.length !== valueIds.length) {
    const cyclic = findCircularValues({
      unresolved: valueIds.filter((valueId) => !order.includes(valueId)),
      dependencies,
    });

    return {
      success: false,
      error: `values: ${cyclic.join(", ")} depend on each other in a circle — one of them has to be computed from inputs alone`,
    };
  }

  return { success: true, order };
};

interface IResolveInputValueProps {
  input: TUiInput;
  inputState?: ReadonlyMap<string, TUiScalar>;
}

interface IIsNumberAllowedProps {
  input: TUiInput;
  value: number;
}

// NOTE: A stale or hand-made state entry must not take a slider past its
// bounds or a select to a value it does not offer — the spec's own value wins.
const isNumberAllowed = ({ input, value }: IIsNumberAllowedProps): boolean => {
  if (!Number.isFinite(value)) {
    return false;
  }

  switch (input.kind) {
    case EUiInputKind.SELECT:
      return input.options.some((option) => option.value === value);
    case EUiInputKind.SLIDER:
    case EUiInputKind.NUMBER:
      return (
        (input.min === undefined || value >= input.min) &&
        (input.max === undefined || value <= input.max)
      );
    default:
      return true;
  }
};

const resolveInputValue = ({
  input,
  inputState,
}: IResolveInputValueProps): TUiScalar => {
  const changed = inputState?.get(input.id);

  if (changed === undefined) {
    return input.value;
  }

  // NOTE: A changed value is used only if it has the input's own type, so a
  // stale state entry from another spec cannot turn a toggle into a number.
  if (typeof changed !== typeof input.value) {
    return input.value;
  }

  if (typeof changed === "boolean") {
    return changed;
  }

  return isNumberAllowed({ input, value: changed }) ? changed : input.value;
};

// NOTE: The scope every formula in the spec is evaluated in. Built from the
// inputs and the ordered values; a spec that failed validation resolves to the
// inputs alone, so a renderer still has something to show.
export const resolveUiScope = ({
  spec,
  inputState,
}: IResolveUiScopeProps): Map<string, TUiScalar> => {
  const scope = new Map<string, TUiScalar>();

  for (const input of spec.inputs ?? []) {
    scope.set(input.id, resolveInputValue({ input, inputState }));
  }

  const values = spec.values ?? {};
  const ordered = orderUiValues({
    values,
    inputIds: new Set(scope.keys()),
  });

  if (!ordered.success) {
    return scope;
  }

  for (const valueId of ordered.order) {
    scope.set(
      valueId,
      resolveUiValue({ expression: values[valueId], scope }) ?? Number.NaN,
    );
  }

  return scope;
};

// NOTE: One formula (a stats item, a plot bound) against a resolved scope.
// Returns null when the text does not parse — the renderer shows "—".
export const resolveUiValue = ({
  expression,
  scope,
}: IResolveUiValueProps): TUiScalar | null => {
  const parsed = parseUiExpression(expression);

  if (!parsed.success) {
    return null;
  }

  return evaluateUiExpression({ node: parsed.node, scope });
};

// NOTE: A scope with the plot variable bound — the scope a series' `y`
// formula is evaluated in at one point of the x axis.
export const withUiPlotVariable = ({
  scope,
  xValue,
}: IWithUiPlotVariableProps): Map<string, TUiScalar> => {
  const bound = new Map(scope);
  bound.set(UI_PLOT_VARIABLE, xValue);

  return bound;
};
